import { useCallback } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeleteSpaceContribution } from '@/hooks/useSpaceContributions'
import type { SpaceContributionTableRow } from '@/types/space-contributions'

interface DeleteSpaceContributionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  spaceContribution: SpaceContributionTableRow | null
}

export function DeleteSpaceContributionDialog({
  open,
  onOpenChange,
  spaceContribution,
}: DeleteSpaceContributionDialogProps) {
  const deleteMutation = useDeleteSpaceContribution()

  const handleDelete = useCallback(() => {
    if (!spaceContribution) return

    deleteMutation.mutate(spaceContribution.id, {
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }, [spaceContribution, deleteMutation, onOpenChange])

  if (!spaceContribution) return null

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Space Contribution?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this space contribution? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="rounded-lg bg-muted p-3 text-sm mb-4">
          <p className="font-medium">{spaceContribution.unit_name || 'Unknown Unit'}</p>
          <p className="text-muted-foreground text-xs">
            {spaceContribution.available_from} to {spaceContribution.available_until}
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
